import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    CircularProgress, TextField, MenuItem, Box, Grid, Typography,
    Divider, Paper, Stepper, Step, StepLabel, Avatar, Stack
} from '@mui/material';
import { toast } from 'sonner';
import { useAuthStore } from '../../../store/useAuthStore';
import apiClient from '../../../services/api-client';

// Icônes
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoneyIcon from '@mui/icons-material/Money';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SearchIcon from '@mui/icons-material/Search';
import PaymentIcon from '@mui/icons-material/Payment';
import { SmartphoneIcon } from 'lucide-react';

const COLORS = {
    gold: '#c9a84c',
    goldDark: '#b28f34',
    navy: '#1a1a2e',
    bg: '#faf7f2',
    border: 'rgba(201,168,76,0.15)'
};

const methodConfig = {
    'ESPECES': { label: 'Espèces', icon: <MoneyIcon sx={{ fontSize: 18 }} />, color: '#2e7d32' },
    'CARTE_BANCAIRE': { label: 'Carte', icon: <CreditCardIcon sx={{ fontSize: 18 }} />, color: '#1565c0' },
    'VIREMENT': { label: 'Virement', icon: <AccountBalanceIcon sx={{ fontSize: 18 }} />, color: '#6a1b9a' },
    'CHEQUE': { label: 'Chèque', icon: <ApartmentIcon sx={{ fontSize: 18 }} />, color: '#ef6c00' },
    'MOBILE_MONEY': { label: 'Mobile', icon: <SmartphoneIcon sx={{ fontSize: 18 }} />, color: '#388e3c' },
};

const steps = ['Recherche', 'Informations', 'Confirmation'];

export const AddPaymentDialog = ({ open, onOpenChange, onSuccess }) => {
    const [activeStep, setActiveStep] = useState(0);

    // Étape 1 : Recherche
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [invoice, setInvoice] = useState(null);
    const [details, setDetails] = useState(null);

    // Étape 2 : Formulaire de paiement
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('ESPECES');
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const remaining = invoice
        ? Math.max(0, (invoice.montantTotal || 0) - (invoice.montantPaye || 0))
        : 0;

    // Reset des états à la fermeture/ouverture
    useEffect(() => {
        if (open) {
            setActiveStep(0);
            setSearchQuery('');
            setInvoice(null);
            setDetails(null);
            setAmount('');
            setMethod('ESPECES');
            setReference('');
            setNotes('');
        }
    }, [open]);

    // Action Étape 1 : Recherche de la facture/dossier
    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        setIsSearching(true);
        try {
            // Appel à ton API de recherche (par numéro de facture ou référence dossier)
            const res = await apiClient.get(`/api/invoices/lookup`, {
                params: { query: searchQuery.trim() }
            });

            if (res.data) {
                setInvoice(res.data.invoice || res.data);
                setDetails(res.data.details || res.data);
                setActiveStep(1); // Passage à l'étape formulaire
            } else {
                toast.error("Aucune facture correspondante trouvée.");
            }
        } catch (error) {
            console.error('Error fetching invoice:', error);
            toast.error(error.response?.data?.message || "Référence introuvable ou incorrecte.");
        } finally {
            setIsSearching(false);
        }
    };

    // Action Étape 2 : Enregistrement du paiement
    const handleSubmit = async () => {
        if (!invoice || !amount || Number(amount) <= 0) return;

        if (Number(amount) > remaining) {
            toast.error(`Le montant dépasse le reste à payer (${remaining.toLocaleString('fr-FR')} FCFA)`);
            return;
        }

        setIsSubmitting(true);
        try {
            await apiClient.post(`/api/paiements/facture/${invoice.id}`, {
                typeOperation: 'ENCAISSEMENT',
                montant: Number(amount),
                modePaiement: method,
                referenceTransaction: reference || undefined,
                commentaire: notes || undefined,
            });

            setActiveStep(2); // Passage à l'étape 3 (Succès)
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error("Erreur lors de l'enregistrement:", err);
            toast.error(err.response?.data?.message || "Erreur lors de l'enregistrement");
        } finally {
            setIsSubmitting(false);
        }
    };

    const quickAmounts = invoice
        ? [
            { label: '25%', value: (remaining * 0.25).toFixed(2) },
            { label: '50%', value: (remaining * 0.5).toFixed(2) },
            { label: 'Solde', value: remaining.toFixed(2) },
        ]
        : [];

    return (
        <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="sm" fullWidth
            PaperProps={{ sx: { backgroundColor: COLORS.bg, color: COLORS.navy, borderRadius: 3 } }}
        >
            <Box sx={{ p: 2 }}>
                <DialogTitle sx={{ color: COLORS.navy, display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 700 }}>
                    <PaymentIcon sx={{ color: COLORS.gold, fontSize: 24 }} />
                    Guichet d'encaissement direct
                </DialogTitle>

                {/* Stepper horizontal indicateur de progression */}
                <Box sx={{ width: '100%', my: 2 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label} sx={{
                                '& .MuiStepLabel-label.Mui-active': { color: COLORS.gold, fontWeight: 600 },
                                '& .MuiStepIcon-root.Mui-active': { color: COLORS.gold },
                                '& .MuiStepIcon-root.Mui-completed': { color: COLORS.navy }
                            }}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <DialogContent dividers sx={{ borderColor: COLORS.border, py: 3 }}>

                    {/* ÉTAPE 1 : FORMULAIRE DE RECHERCHE */}
                    {activeStep === 0 && (
                        <Stack spacing={3} sx={{ pt: 1 }}>
                            <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                                Saisissez le **numéro de facture** ou la **référence du dossier** pour récupérer les données financières associées.
                            </Typography>
                            <TextField
                                fullWidth
                                label="Référence Facture ou Dossier"
                                placeholder="ex: FACT-2026-0001 ou DOS-78A9"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: isSearching ? <CircularProgress size={20} /> : <SearchIcon sx={{ color: COLORS.textMuted }} />,
                                    sx: { backgroundColor: 'white', borderRadius: 0.5 }
                                }}
                            />
                        </Stack>
                    )}

                    {/* ÉTAPE 2 : INFORMATIONS ET FORMULAIRE DE PAIEMENT */}
                    {activeStep === 1 && invoice && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <Paper elevation={0} sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, p: 2, bgcolor: '#fff' }}>
                                {[
                                    { label: 'Facture', value: details?.numeroFacture || invoice.numeroFacture, valueStyle: { color: COLORS.gold, fontWeight: 600 } },
                                    { label: 'Client', value: details?.clientInfo?.nomComplet || details?.clientName, valueStyle: {} },
                                    { label: 'Dossier', value: details?.dossierInfo?.referenceCode || details?.reference, valueStyle: {} },
                                ].map(({ label, value, valueStyle }) => (
                                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                        <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>{label}</Typography>
                                        <Typography variant="caption" sx={{ color: COLORS.navy, ...valueStyle }}>{value || 'N/A'}</Typography>
                                    </Box>
                                ))}
                                <Divider sx={{ my: 1, borderColor: COLORS.border }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Total TTC</Typography>
                                    <Typography variant="caption" sx={{ color: COLORS.navy, fontWeight: 600 }}>{(invoice.montantTotal || 0).toLocaleString('fr-FR')} <span className='text-xs text-gray-400'>FCFA</span></Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Total payé</Typography>
                                    <Typography variant="caption" sx={{ color: COLORS.navy, fontWeight: 600 }}>{(invoice.montantPaye || 0).toLocaleString('fr-FR')} <span className='text-xs text-gray-400'>FCFA</span></Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Reste à payer</Typography>
                                    <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 600 }}>{remaining.toLocaleString('fr-FR')} <span className='text-xs text-red-400'>FCFA</span></Typography>
                                </Box>
                            </Paper>

                            {/* Saisie montant */}
                            <Box>
                                <Typography variant="body2" sx={{ color: COLORS.navy, mb: 1, fontWeight: 500 }}>Montant du versement</Typography>
                                <TextField
                                    type="number" fullWidth placeholder="0.00" value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    inputProps={{ min: 0.01, max: remaining, step: 0.01 }}
                                    InputProps={{
                                        endAdornment: <Typography sx={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 600 }}>FCFA</Typography>,
                                        sx: { fontSize: 16, fontWeight: 600, backgroundColor: 'white', borderRadius: 0.5 }
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    {quickAmounts.map((qa) => (
                                        <Button key={qa.label} variant="outlined" size="small"
                                            sx={{ flex: 1, fontSize: 11, height: 28, borderColor: COLORS.border, color: COLORS.textSecondary, textTransform: 'none', borderRadius: 0.5, '&:hover': { borderColor: COLORS.gold, color: COLORS.gold } }}
                                            onClick={() => setAmount(qa.value)}
                                        >
                                            {qa.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>

                            {/* Mode de paiement */}
                            <Box>
                                <Typography variant="body2" sx={{ color: COLORS.navy, mb: 1, fontWeight: 500 }}>Mode de paiement</Typography>
                                <Grid container spacing={1}>
                                    {Object.entries(methodConfig).map(([key, config]) => (
                                        <Grid item xs={4} key={key}>
                                            <Box onClick={() => setMethod(key)}
                                                sx={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, p: 1, borderRadius: 0.5, cursor: 'pointer', transition: 'all 0.15s',
                                                    border: `1px solid ${method === key ? config.color : 'rgba(0,0,0,0.08)'}`,
                                                    backgroundColor: method === key ? `${config.color}08` : 'white',
                                                    color: method === key ? config.color : '#666',
                                                    '&:hover': { borderColor: config.color }
                                                }}
                                            >
                                                {config.icon}
                                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 9 }}>{config.label}</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>

                            {/* Champs optionnels conditionnels */}
                            {method !== "ESPECES" && (
                                <TextField fullWidth label="Référence de transaction" placeholder="N° chèque, ID virement, Ref Mobile Money..." value={reference} onChange={(e) => setReference(e.target.value)} size="small" InputProps={{ sx: { backgroundColor: 'white', borderRadius: 0.5 } }} />
                            )}
                            <TextField fullWidth multiline rows={2} label="Commentaire (optionnel)" value={notes} onChange={(e) => setNotes(e.target.value)} size="small" InputProps={{ sx: { backgroundColor: 'white', borderRadius: 0.5 } }} />
                        </Box>
                    )}

                    {/* ÉTAPE 3 : CONFIRMATION ET SUCCÈS */}
                    {activeStep === 2 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'rgba(46,125,50,0.1)', color: '#2e7d32', width: 64, height: 64, mb: 1 }}>
                                <CheckCircleIcon sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700} align="center">Encaissé avec succès !</Typography>
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ maxWidth: 300 }}>
                                Le versement de **{Number(amount).toLocaleString('fr-FR')} FCFA** a bien été imputé sur la facture.
                            </Typography>
                        </Box>
                    )}

                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, pt: 2, gap: 1 }}>
                    {activeStep < 2 && (
                        <Button onClick={() => onOpenChange(false)} variant="outlined" sx={{ color: '#666', borderColor: 'rgba(0,0,0,0.12)', textTransform: 'none' }}>
                            Annuler
                        </Button>
                    )}

                    {activeStep === 0 && (
                        <Button onClick={handleSearch} disabled={!searchQuery.trim() || isSearching} variant="contained" sx={{ bgcolor: COLORS.navy, color: 'white', textTransform: 'none', '&:hover': { bgcolor: '#0f0f1c' } }}>
                            {isSearching ? 'Vérification...' : 'Rechercher'}
                        </Button>
                    )}

                    {activeStep === 1 && (
                        <>
                            <Button onClick={() => setActiveStep(0)} sx={{ color: COLORS.navy, textTransform: 'none' }}>
                                Retour
                            </Button>
                            <Button onClick={handleSubmit} disabled={!amount || Number(amount) <= 0 || isSubmitting} startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                                sx={{ background: 'linear-gradient(135deg, #b08d3a, #c9a84c)', color: 'white', textTransform: 'none', borderRadius: 0.5, '&:hover': { background: 'linear-gradient(135deg, #9a7a30, #b8983e)' } }}
                            >
                                {isSubmitting ? 'Traitement...' : 'Confirmer l\'encaissement'}
                            </Button>
                        </>
                    )}

                    {activeStep === 2 && (
                        <Button onClick={() => onOpenChange(false)} variant="contained" sx={{ bgcolor: COLORS.navy, color: 'white', textTransform: 'none', width: '100%' }}>
                            Fermer le guichet
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
};